import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, KeyRound, Check } from "lucide-react";
import { PasswordInput } from "@/components/site/PasswordInput";
import { API_BASE } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [nw, setNw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (nw.length < 6) return toast.error("Password must be at least 6 characters.");
    if (nw !== confirm) return toast.error("Passwords don't match.");
    setLoading(true);
    try {
      await axios.post(`${API_BASE}/member/auth/reset-password`, { token, new_password: nw });
      setDone(true);
      toast.success("Password updated — log in with your new password.");
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset your password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0] text-[#2D372B]">
      <div className="max-w-md mx-auto px-6 pt-16 pb-20">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-[#89937C]/20 flex items-center justify-center"><KeyRound size={20} className="text-[#5C665A]" /></div>
          <h1 className="font-serif-display text-3xl">Set a new password</h1>
        </div>
        {!token ? (
          <div className="mt-8 bg-white border border-[#E5E2D9] rounded-3xl p-7" data-testid="reset-no-token">
            <p className="text-sm text-[#5C665A]">This reset link is incomplete. Please use the link from your email, or request a new one.</p>
            <Link to="/forgot-password" className="mt-4 inline-flex rounded-full px-6 py-2.5 bg-[#2D372B] text-white text-sm font-medium hover:bg-[#89937C] transition-colors">Request a new link</Link>
          </div>
        ) : done ? (
          <div className="mt-8 bg-white border-2 border-[#89937C] rounded-3xl p-7" data-testid="reset-success">
            <Check size={22} className="text-[#5C665A]" />
            <div className="font-semibold mt-3">Password updated</div>
            <p className="text-sm text-[#5C665A] mt-1">Taking you to the login page…</p>
            <Link to="/login" className="mt-4 inline-flex rounded-full px-6 py-2.5 bg-[#2D372B] text-white text-sm font-medium hover:bg-[#89937C] transition-colors">Log in now</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 bg-white border border-[#E5E2D9] rounded-3xl p-7 space-y-4">
            <PasswordInput data-testid="reset-new-password" value={nw} onChange={(e) => setNw(e.target.value)} placeholder="New password (min 6 characters)" className="bg-[#F7F5F0] border-[#E5E2D9] h-12" />
            <PasswordInput data-testid="reset-confirm-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm new password" className="bg-[#F7F5F0] border-[#E5E2D9] h-12" />
            <button data-testid="reset-submit-btn" type="submit" disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 bg-[#2D372B] text-white font-medium hover:bg-[#89937C] transition-colors disabled:opacity-60">
              {loading && <Loader2 size={14} className="animate-spin" />} Update my password
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
